import type React from 'react';
import {
  Modal,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  FormGroup,
  Button,
  Skeleton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useState } from 'react';
import { Plus, X, History } from 'lucide-react';
import type { IUserViewModel } from 'viewModels';
import {
  CloseButton,
  ModalHeader,
  ModalTitle,
  ModalTitleContainer,
  ModalFooter,
  ModalContent,
  CardsSection,
  ChartSection,
  ContentGrid,
  TransactionContainer,
  TransactionList,
  TransactionItem,
  StatusBadge,
  TransactionInfo,
  TransactionDate,
  TransactionDescription,
  TransactionAmount,
  CreditContainer,
  CreditForm,
  Label,
  CreditHistory,
  HistoryTitle,
  HistoryItem,
  HistoryDescription,
  HistoryDate,
  HistoryAmount,
} from './styles';
import WalletOutlinedIcon from '@mui/icons-material/WalletOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TuneIcon from '@mui/icons-material/Tune';
import KpiCard from 'components/KpiCard';
import { DualAxes } from '@ant-design/plots';
import { Empty } from 'antd';
import { useCompanyUser } from 'contexts';
import { TransactionType } from 'types/enums';
import TextArea from 'antd/es/input/TextArea';

interface ModalProps {
  open: boolean;
  onClose: () => void;
}

export default function CompanyManagerModal({ open, onClose }: ModalProps) {
  const [tabValue, setTabValue] = useState(0);
  const [creditValue, setCreditValue] = useState(0);
  const [defaultValueInput, setDefaultValueInput] = useState('');

  const {
    selectedUserId,
    companyUsers,
    loadingUserHistory,
    loadingUserTransactions,
    addUserCredit,
    loadingAddCredit,
  } = useCompanyUser();

  const user = companyUsers?.find(
    (u: IUserViewModel) => u.id === selectedUserId
  ) as IUserViewModel;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddCredit = async () => {
    if (!user || !creditValue) return;

    await addUserCredit({
      userId: user.id,
      amount: Math.round(creditValue * 100),
      currency: 'BRL',
    });

    setCreditValue(0);
  };

  const config = {
    data: user.financialHistory || [],
    xField: 'data',
    legend: true,
    responsive: true,
    autoFit: true,
    children: [
      {
        type: 'area',
        yField: 'debitos',
        shapeField: 'smooth',
        style: {
          fill: 'rgba(91, 143, 249, 0.4)',
          stroke: '#5B8FF9',
          lineWidth: 2,
        },
        label: {
          style: {
            dy: -30,
            dx: -30,
            textAlign: 'middle',
          },
          text: (datum: any) => `R$${datum.debitos}`,
        },
        axis: {
          y: {
            title: 'Débitos',
            style: { titleFill: '#5B8FF9' },
          },
        },
      },
      {
        type: 'area',
        yField: 'creditos',
        shapeField: 'smooth',
        style: {
          fill: 'rgba(90, 216, 166, 0.4)',
          stroke: '#5AD8A6',
          lineWidth: 2,
        },
        label: {
          style: {
            dy: -20,
            dx: -30,
            textAlign: 'middle',
          },
          text: (datum: any) => `R$${datum.creditos}`,
        },
        axis: {
          y: {
            position: 'right',
            title: 'Créditos',
            style: { titleFill: '#5AD8A6' },
          },
        },
      },
    ],
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{ display: 'flex', flexDirection: 'column' }}
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          outline: 'none',
          width: { xs: '95vw', sm: '90vw', md: '80vw', lg: '70vw' },
          height: { xs: '90vh', sm: '85vh' },
          maxWidth: '1200px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ModalHeader>
          <ModalTitleContainer>
            <ModalTitle id="modal-title">Gerenciar Usuário</ModalTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Gerencie os detalhes de {user.name}
            </Typography>
          </ModalTitleContainer>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="user management tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.95rem',
              },
            }}
          >
            <Tab
              label="Visão geral"
              id="user-tab-0"
              aria-controls="user-tabpanel-0"
            />
            <Tab
              label="Transações"
              id="user-tab-1"
              aria-controls="user-tabpanel-1"
            />
            <Tab
              label="Créditos"
              id="user-tab-2"
              aria-controls="user-tabpanel-2"
            />
          </Tabs>
        </Box>

        <ModalContent>
          {tabValue === 0 && (
            <div>
              <ContentGrid>
                <CardsSection>
                  <KpiCard
                    title="Balanço"
                    value={
                      user.financialCardsData?.balance ? user.balance / 100 : 0
                    }
                    currency
                    icon={<WalletOutlinedIcon />}
                  />
                  <KpiCard
                    title="Transações"
                    value={user.financialCardsData?.totalTransactions ?? 0}
                    icon={<TuneIcon />}
                    backgroundColor="#af1a58"
                  />
                  <KpiCard
                    title="Recarga Mensal"
                    value={user.financialCardsData?.currentMonthCredits ?? 0}
                    currency
                    icon={<InsightsIcon />}
                    backgroundColor="#107066"
                  />
                  <KpiCard
                    title="Consumo Mensal"
                    value={user.financialCardsData?.currentMonthSpent ?? 0}
                    currency
                    icon={<ReceiptLongIcon />}
                    backgroundColor="#bd8102"
                  />
                </CardsSection>
                <ChartSection>
                  <h3>Histórico de consumo mensal</h3>
                  <div className="chart-container">
                    {user.financialHistory &&
                    user.financialHistory?.length > 0 ? (
                      <DualAxes {...config} />
                    ) : (
                      <Empty
                        description="Nenhum dado encontrado para este usuário."
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                      />
                    )}
                  </div>
                </ChartSection>
              </ContentGrid>
            </div>
          )}

          {tabValue === 1 && (
            <div>
              <TransactionContainer>
                {loadingUserTransactions ? (
                  <>
                    {[...Array(4)].map((_, i) => (
                      <TransactionItem key={i}>
                        <Skeleton width={80} height={24} />
                        <TransactionInfo>
                          <Skeleton width="60%" />
                          <Skeleton width="40%" />
                        </TransactionInfo>
                        <Skeleton width={70} height={24} />
                      </TransactionItem>
                    ))}
                  </>
                ) : user.transactions?.length ? (
                  <TransactionList>
                    {user.transactions.map(transaction => (
                      <TransactionItem key={transaction.id}>
                        <StatusBadge type={transaction.transactionType}>
                          {transaction.transactionType === TransactionType.DEBIT
                            ? 'DÉBITO'
                            : 'CRÉDITO'}
                        </StatusBadge>
                        <TransactionInfo>
                          <TransactionDescription>
                            {transaction.description}
                          </TransactionDescription>
                          <TransactionDate>
                            {new Date(transaction.createdAt).toLocaleDateString(
                              'pt-BR'
                            )}
                          </TransactionDate>
                        </TransactionInfo>
                        <TransactionAmount type={transaction.transactionType}>
                          {transaction.transactionType === TransactionType.DEBIT
                            ? '-'
                            : '+'}
                          R$ {(transaction.amount / 100).toFixed(2)}
                        </TransactionAmount>
                      </TransactionItem>
                    ))}
                  </TransactionList>
                ) : (
                  <Empty
                    description="Nenhuma transação encontrada"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </TransactionContainer>
            </div>
          )}

          {tabValue === 2 && (
            <div>
              <CreditContainer>
                <CreditForm>
                  <FormGroup>
                    <Label>Valor do Crédito</Label>
                    <TextField
                      label="Valor"
                      type="text"
                      value={defaultValueInput}
                      onChange={e => {
                        const input = e.target.value;

                        if (!/^\d*(,\d{0,2})?$/.test(input)) return;

                        setDefaultValueInput(input);

                        const normalized = input.replace(',', '.');
                        const value = parseFloat(normalized);

                        if (!isNaN(value)) {
                          setCreditValue(value);
                        } else {
                          setCreditValue(0); // ou null
                        }
                      }}
                      required
                      fullWidth
                      size="small"
                      variant="outlined"
                      inputProps={{
                        inputMode: 'decimal',
                        pattern: '[0-9]+([,][0-9]{0,2})?',
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">R$</InputAdornment>
                        ),
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Descrição</Label>
                    <TextArea
                      placeholder="Descreva o motivo da adição de crédito..."
                      rows={3}
                    />
                  </FormGroup>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleAddCredit}
                    disabled={loadingAddCredit || creditValue <= 0}
                  >
                    {loadingAddCredit ? (
                      <Skeleton
                        width={16}
                        height={16}
                        variant="circular"
                        sx={{ mr: 1 }}
                      />
                    ) : (
                      <Plus size={16} style={{ marginRight: 8 }} />
                    )}
                    Adicionar Crédito
                  </Button>
                </CreditForm>

                <CreditHistory>
                  <HistoryTitle>
                    <History size={20} />
                    Histórico de Créditos
                  </HistoryTitle>

                  {loadingUserHistory ? (
                    [...Array(4)].map((_, i) => (
                      <HistoryItem key={i}>
                        <div>
                          <Skeleton width={120} />
                          <Skeleton width={80} />
                        </div>
                        <Skeleton width={60} />
                      </HistoryItem>
                    ))
                  ) : user.transactions?.filter(
                      t => t.transactionType === TransactionType.CREDIT
                    ).length ? (
                    user.transactions
                      .filter(t => t.transactionType === TransactionType.CREDIT)
                      .map(record => (
                        <HistoryItem key={record.id}>
                          <div>
                            <HistoryDescription>
                              {record.description}
                            </HistoryDescription>
                            <HistoryDate>
                              {new Date(record.createdAt).toLocaleDateString(
                                'pt-BR'
                              )}{' '}
                              • Adicionado por
                            </HistoryDate>
                          </div>
                          <HistoryAmount>
                            +R$ {(record.amount / 100).toFixed(2)}
                          </HistoryAmount>
                        </HistoryItem>
                      ))
                  ) : (
                    <Empty
                      description="Nenhum crédito encontrado"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </CreditHistory>
              </CreditContainer>
            </div>
          )}
        </ModalContent>
        <ModalFooter />
      </Paper>
    </Modal>
  );
}
